import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { PRODUCT_CATEGORIES, COUNTRIES } from '../data/mockData';
import { useSubmitRfq } from '../data/hooks';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';
import RfqTrackingShare from '../components/RfqTrackingShare';

const STEPS = [
  { id: 1, label: 'Product' },
  { id: 2, label: 'Specifications' },
  { id: 3, label: 'Quantity & Branding' },
  { id: 4, label: 'Shipping & Timeline' },
  { id: 5, label: 'Contact' },
];

// Bucket numerical quantities into a privacy-conscious band (the exact figure
// is buyer-sensitive; the band is sufficient for funnel analytics).
const bucketQuantity = (n) => {
  if (!n) return 'unspecified';
  if (n < 250)   return '<250';
  if (n < 500)   return '250-499';
  if (n < 1000)  return '500-999';
  if (n < 5000)  return '1k-5k';
  if (n < 20000) return '5k-20k';
  return '20k+';
};

const RFQForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [reference, setReference] = useState(null);
  const { mutate: submitRfq, loading: submitting, error: submitError, submitted } = useSubmitRfq();

  // RFQ start — fire once on first mount
  useEffect(() => {
    track(EVENTS.RFQ_STARTED, { prefilled_product: !!searchParams.get('product') });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState({
    category: searchParams.get('product') ? '' : '',
    productName: searchParams.get('product') || '',
    materials: '',
    qualityNotes: '',
    quantity: '',
    sampleNeeded: 'yes',
    branding: '',
    packaging: '',
    destination: '',
    targetPrice: '',
    timeline: '',
    name: '',
    company: '',
    email: '',
    phone: '',
  });


  const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const validateStep = () => {
    if (step === 1 && (!data.category || !data.productName)) { toast.error('Please fill the product category and name.'); return false; }
    if (step === 3 && !data.quantity) { toast.error('Please add an estimated quantity.'); return false; }
    if (step === 5 && (!data.name || !data.email)) { toast.error('Please add your name and email.'); return false; }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < STEPS.length) setStep(step + 1);
    else submit();
  };
  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    try {
      const payload = {
        buyer_name: data.name,
        buyer_email: data.email,
        buyer_company: data.company,
        product_type: `${data.category}: ${data.productName}`,
        target_quantity: parseInt(data.quantity, 10) || null,
        target_unit_price: data.targetPrice,
        shipping_destination: data.destination,
        notes: [
          data.materials && `Materials: ${data.materials}`,
          data.qualityNotes && `Quality: ${data.qualityNotes}`,
          data.branding && `Branding: ${data.branding}`,
          data.packaging && `Packaging: ${data.packaging}`,
          data.timeline && `Timeline: ${data.timeline}`,
          data.sampleNeeded && `Sample required: ${data.sampleNeeded}`,
          data.phone && `Phone: ${data.phone}`,
        ].filter(Boolean).join('\n'),
        source_page: 'rfq',
      };
      const result = await submitRfq(payload);
      setReference(result?.reference || null);
      track(EVENTS.RFQ_SUBMITTED, {
        reference: result?.reference,
        product_category: data.category,
        quantity_band: bucketQuantity(parseInt(data.quantity, 10) || 0),
        has_destination: !!data.destination,
      });
      toast.success(`RFQ submitted — reference ${result?.reference || 'recorded'}.`);
    } catch (err) {
      toast.error('Failed to submit RFQ. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="bg-white" data-testid="rfq-success">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center md:py-28">
          <div className="mx-auto flex h-16 w-16 items-center justify-center border-2 border-black">
            <Check size={28} strokeWidth={1.5} />
          </div>
          <span className="mt-8 inline-block label-overline">RFQ Received</span>
          <h1 className="mt-4 font-display text-4xl tracking-tighter md:text-6xl" style={{ fontWeight: 700 }}>
            Your brief is in.
          </h1>
          <p className="mt-5 text-neutral-700">
            Thank you, {data.name || 'there'}. Your quote request has been logged{reference ? ` under reference ` : '.'}{reference && <span className="font-mono-data text-black">{reference}</span>}. The GIWAHS sourcing team will follow up by email within one business day.
          </p>

          {/* QR + share card — only when a reference exists */}
          {reference && (
            <div className="mt-10 text-left">
              <RfqTrackingShare reference={reference} />
            </div>
          )}

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            {reference && (
              <button
                onClick={() => navigate(`/rfq/${reference}`)}
                data-testid="rfq-track-link"
                className="btn-giwahs btn-giwahs-primary"
              >
                Open private dashboard
              </button>
            )}
            <button onClick={() => navigate('/factories')} className="btn-giwahs btn-giwahs-secondary">Browse factories</button>
            <button onClick={() => { setStep(1); window.location.reload(); }} data-testid="rfq-new" className="btn-giwahs btn-giwahs-secondary">Submit another</button>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = "w-full border border-black bg-white px-3 py-3 text-sm placeholder:text-neutral-400 focus:outline-none";
  const labelClass = "label-overline";

  return (
    <div data-testid="rfq-page" className="bg-white">
      <SEO
        title="Request a Quote — Source from Verified Factories"
        description="Submit a guided 5-step RFQ and get matched with up to 6 verified factories within 48 hours. Free, no commitment, mobile-friendly."
      />
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ Request for Quote</span>
          <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
            Tell us what you're sourcing.
          </h1>
          <p className="mt-4 max-w-2xl text-neutral-700">
            A guided 5-step brief — takes ~3 minutes. Submit once, get matched with up to 6 factories.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16">
          {/* Step indicator — FGSD-001 final: wraps cleanly at every width,
              no horizontal clipping (previously overflow-x-auto clipped the
              final steps below ~900px) */}
          <div className="flex flex-wrap items-center gap-2">
            {STEPS.map((s) => (
              <div key={s.id} className={`flex shrink-0 items-center gap-2 border px-3 py-2 ${step === s.id ? 'border-black bg-black text-white' : step > s.id ? 'border-black bg-white text-black' : 'border-neutral-300 bg-white text-neutral-400'}`} data-testid={`rfq-step-indicator-${s.id}`}>
                <span className="font-mono-data text-xs">{String(s.id).padStart(2, '0')}</span>
                <span className="label-overline">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-black p-6 md:p-10">
            {step === 1 && (
              <div data-testid="rfq-step-1" className="space-y-5">
                <h2 className="font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>What are you sourcing?</h2>
                <div>
                  <label className={labelClass}>Product Category</label>
                  <select data-testid="rfq-category" value={data.category} onChange={(e) => update('category', e.target.value)} className={`${inputClass} mt-2`}>
                    <option value="">Select a category</option>
                    {PRODUCT_CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Product Name / Reference</label>
                  <input data-testid="rfq-product-name" value={data.productName} onChange={(e) => update('productName', e.target.value)} placeholder="e.g. Oversized heavyweight hoodie, 480gsm" className={`${inputClass} mt-2`} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="rfq-step-2" className="space-y-5">
                <h2 className="font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>Material & quality.</h2>
                <div>
                  <label className={labelClass}>Materials / Fabric</label>
                  <input data-testid="rfq-materials" value={data.materials} onChange={(e) => update('materials', e.target.value)} placeholder="e.g. 80% organic cotton, 20% recycled polyester" className={`${inputClass} mt-2`} />
                </div>
                <div>
                  <label className={labelClass}>Quality notes</label>
                  <textarea data-testid="rfq-quality" value={data.qualityNotes} onChange={(e) => update('qualityNotes', e.target.value)} placeholder="e.g. heavyweight, garment dyed, premium fit" rows={4} className={`${inputClass} mt-2`} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="rfq-step-3" className="space-y-5">
                <h2 className="font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>Quantity & branding.</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Estimated quantity (units)</label>
                    <input data-testid="rfq-quantity" value={data.quantity} onChange={(e) => update('quantity', e.target.value)} placeholder="e.g. 500" className={`${inputClass} mt-2`} />
                  </div>
                  <div>
                    <label className={labelClass}>Need a sample?</label>
                    <select data-testid="rfq-sample" value={data.sampleNeeded} onChange={(e) => update('sampleNeeded', e.target.value)} className={`${inputClass} mt-2`}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Branding requirements</label>
                  <input data-testid="rfq-branding" value={data.branding} onChange={(e) => update('branding', e.target.value)} placeholder="e.g. woven label, embroidered logo, neck print" className={`${inputClass} mt-2`} />
                </div>
                <div>
                  <label className={labelClass}>Packaging requirements</label>
                  <input data-testid="rfq-packaging" value={data.packaging} onChange={(e) => update('packaging', e.target.value)} placeholder="e.g. polybag + hangtag + custom box" className={`${inputClass} mt-2`} />
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="rfq-step-4" className="space-y-5">
                <h2 className="font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>Shipping & timeline.</h2>
                <div>
                  <label className={labelClass}>Shipping destination</label>
                  <select data-testid="rfq-destination" value={data.destination} onChange={(e) => update('destination', e.target.value)} className={`${inputClass} mt-2`}>
                    <option value="">Select country</option>
                    {COUNTRIES.concat(['USA', 'UK', 'Germany', 'Australia']).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Target unit price (USD)</label>
                    <input data-testid="rfq-target-price" value={data.targetPrice} onChange={(e) => update('targetPrice', e.target.value)} placeholder="e.g. $14.50" className={`${inputClass} mt-2`} />
                  </div>
                  <div>
                    <label className={labelClass}>Timeline / delivery by</label>
                    <input data-testid="rfq-timeline" value={data.timeline} onChange={(e) => update('timeline', e.target.value)} placeholder="e.g. June 2026" className={`${inputClass} mt-2`} />
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="rfq-step-5" className="space-y-5">
                <h2 className="font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>Your details.</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Full name</label>
                    <input data-testid="rfq-name" value={data.name} onChange={(e) => update('name', e.target.value)} className={`${inputClass} mt-2`} />
                  </div>
                  <div>
                    <label className={labelClass}>Company / Brand</label>
                    <input data-testid="rfq-company" value={data.company} onChange={(e) => update('company', e.target.value)} className={`${inputClass} mt-2`} />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input data-testid="rfq-email" type="email" value={data.email} onChange={(e) => update('email', e.target.value)} className={`${inputClass} mt-2`} />
                  </div>
                  <div>
                    <label className={labelClass}>Phone (optional)</label>
                    <input data-testid="rfq-phone" value={data.phone} onChange={(e) => update('phone', e.target.value)} className={`${inputClass} mt-2`} />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between border-t border-neutral-200 pt-6">
              <button onClick={back} disabled={step === 1} data-testid="rfq-back" className="inline-flex items-center gap-2 label-overline text-neutral-500 disabled:opacity-30 hover:text-black">
                <ArrowLeft size={14} strokeWidth={1.5} /> Back
              </button>
              <span className="font-mono-data text-xs text-neutral-500">{step} / {STEPS.length}</span>
              <button
                onClick={next}
                data-testid="rfq-next"
                disabled={submitting}
                className="btn-giwahs btn-giwahs-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />}
                {step === STEPS.length ? (submitting ? 'Submitting…' : 'Submit RFQ') : 'Continue'}
                {!submitting && <ArrowRight size={14} strokeWidth={1.5} />}
              </button>
              {submitError && step === STEPS.length && (
                <p className="mt-2 text-xs text-red-700" data-testid="rfq-error">Failed to submit. Please try again.</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RFQForm;
